import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, RotateCcw, Image as ImageIcon, Zap, ZapOff } from 'lucide-react';

/**
 * StoryCamera — Instagram-style.
 *   Tap shutter            = photo
 *   Hold shutter           = start video (up to 30s)
 *   Drag finger up/down    = zoom in/out (while holding)
 *   Release                = stop → navigate to /story/edit with LOCAL blob (no upload yet)
 *   Bottom-left gallery    = choose from Camera Roll
 *
 * NOTE: uploading only happens on "Share to Story" inside the editor.
 */
const MAX_VIDEO_MS = 30000;
const HOLD_THRESHOLD_MS = 260; // longer than this = video, shorter = photo
const ZOOM_STEP_PER_PX = 0.008; // 1px drag ≈ 0.008 zoom delta
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const DOUBLE_TAP_MS = 280;

const vibe = (ms) => { try { navigator.vibrate?.(ms); } catch {} };

export default function StoryCamera() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const galleryRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const holdTimerRef = useRef(null);
  const recordStartRef = useRef(0);
  const stopTimeoutRef = useRef(null);
  const progressRaf = useRef(null);
  const pressStartYRef = useRef(null);
  const baseZoomRef = useRef(1);
  const modeRef = useRef('idle'); // idle | photo-pending | video

  const [facing, setFacing] = useState('environment');
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordProgress, setRecordProgress] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showHint, setShowHint] = useState(true); // hides after first interaction
  const [capturePulse, setCapturePulse] = useState(false);

  // pinch + double-tap on preview
  const previewPointers = useRef(new Map());
  const pinchBaseDist = useRef(null);
  const pinchBaseZoom = useRef(1);
  const lastPreviewTap = useRef(0);

  /* ---------------- lifecycle ---------------- */

  useEffect(() => {
    document.body.classList.add('chat-locked');
    return () => document.body.classList.remove('chat-locked');
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async (which) => {
    stopStream();
    setReady(false);
    setError('');
    setZoom(1);
    try {
      // Request the highest available resolution so the captured photo/video
      // matches the native camera quality. No aspectRatio constraint — that
      // forced the browser to letterbox or upscale, which made the preview
      // look 'zoomed in'. We letterbox in CSS instead (object-fit: contain).
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: which },
          width: { ideal: 1920, max: 3840 },
          height: { ideal: 1080, max: 2160 },
        },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (err) {
      setError(
        err?.name === 'NotAllowedError'
          ? 'Camera permission denied. Use Gallery instead.'
          : err?.name === 'NotFoundError'
          ? 'No camera found. Use Gallery instead.'
          : 'Unable to open camera. Use Gallery instead.'
      );
    }
  };

  useEffect(() => {
    startStream(facing);
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  /* ---------------- zoom ---------------- */

  const applyZoom = (value) => {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, value));
    setZoom(clamped);
    const track = streamRef.current?.getVideoTracks?.()[0];
    const cap = track?.getCapabilities?.();
    if (cap && cap.zoom) {
      const realZoom = Math.min(cap.zoom.max || 4, Math.max(cap.zoom.min || 1, clamped));
      track.applyConstraints({ advanced: [{ zoom: realZoom }] }).catch(() => {});
    }
    // CSS preview always applied to videoRef by render
  };

  /* ---------------- torch / flip ---------------- */

  const toggleFacing = () => {
    if (recording) return;
    setFacing(f => (f === 'environment' ? 'user' : 'environment'));
  };

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    const cap = track.getCapabilities?.();
    try {
      if (cap && cap.torch) {
        await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      }
    } catch {}
    setTorchOn(v => !v);
  };

  /* ---------------- photo capture ---------------- */

  const capturePhotoBlob = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    setFlash(true);
    setCapturePulse(true);
    setShowHint(false);
    setTimeout(() => setFlash(false), 140);
    setTimeout(() => setCapturePulse(false), 220);

    // Capture at the sensor's NATIVE resolution at 9:16 portrait, centered.
    // This is the safe-zone the preview shows (because the preview uses
    // object-fit: cover at 9:16). NO extra zoom crop on top — the user's
    // pinch zoom is already applied to the live track via applyConstraints.
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const targetAspect = 9 / 16;
    let cropW, cropH;
    if (vw / vh > targetAspect) {
      // Sensor is wider than portrait → crop sides
      cropH = vh;
      cropW = Math.round(vh * targetAspect);
    } else {
      // Sensor is taller (rare) → crop top/bottom
      cropW = vw;
      cropH = Math.round(vw / targetAspect);
    }
    const sx = Math.round((vw - cropW) / 2);
    const sy = Math.round((vh - cropH) / 2);

    const canvas = document.createElement('canvas');
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    // High-quality scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    if (facing === 'user') {
      ctx.translate(cropW, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, cropW, cropH, 0, 0, cropW, cropH);
    // Preserve full quality on capture; recompression happens at upload time.
    return await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.98));
  };

  /* ---------------- video recording ---------------- */

  const startRecording = () => {
    if (!streamRef.current || recording) return;
    setShowHint(false);
    const mimeCandidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ];
    let mime = '';
    for (const m of mimeCandidates) {
      try {
        if (window.MediaRecorder?.isTypeSupported?.(m)) { mime = m; break; }
      } catch {}
    }
    try {
      const recorder = mime
        ? new MediaRecorder(streamRef.current, { mimeType: mime })
        : new MediaRecorder(streamRef.current);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = handleRecordingStop;
      recorder.start(200); // gather chunks every 200ms for reliability
      recorderRef.current = recorder;
      recordStartRef.current = Date.now();
      setRecording(true);
      setRecordProgress(0);
      setElapsedMs(0);
      vibe(35);
      tickProgress();
      stopTimeoutRef.current = setTimeout(() => stopRecording(), MAX_VIDEO_MS);
    } catch (err) {
      setError('Video recording not supported on this device.');
      modeRef.current = 'idle';
    }
  };

  const tickProgress = () => {
    const step = () => {
      const elapsed = Date.now() - recordStartRef.current;
      setElapsedMs(elapsed);
      setRecordProgress(Math.min(1, elapsed / MAX_VIDEO_MS));
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        progressRaf.current = requestAnimationFrame(step);
      }
    };
    progressRaf.current = requestAnimationFrame(step);
  };

  const stopRecording = () => {
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') r.stop();
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    if (progressRaf.current) cancelAnimationFrame(progressRaf.current);
    setRecording(false);
  };

  const handleRecordingStop = () => {
    vibe(25);
    const blob = new Blob(chunksRef.current, { type: recorderRef.current?.mimeType || 'video/webm' });
    chunksRef.current = [];
    modeRef.current = 'idle';
    if (!blob.size) {
      setError('Recording too short. Try holding longer.');
      return;
    }
    const elapsed = Date.now() - recordStartRef.current;
    if (elapsed < 900) {
      setError('Recording too short. Hold to record video.');
      return;
    }
    const isMp4 = (blob.type || '').includes('mp4');
    const ext = isMp4 ? 'mp4' : 'webm';
    const file = new File([blob], `story_${Date.now()}.${ext}`, { type: blob.type || `video/${ext}` });
    goToEditor(file, 'video');
  };

  /* ---------------- pinch-to-zoom + double-tap-flip on preview ---------------- */

  const onPreviewPointerDown = (e) => {
    if (!ready) return;
    previewPointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (previewPointers.current.size === 2) {
      const pts = Array.from(previewPointers.current.values());
      const dx = pts[1].x - pts[0].x;
      const dy = pts[1].y - pts[0].y;
      pinchBaseDist.current = Math.hypot(dx, dy) || 1;
      pinchBaseZoom.current = zoom;
    } else if (previewPointers.current.size === 1) {
      // double-tap detection (single pointer)
      const now = Date.now();
      if (now - lastPreviewTap.current < DOUBLE_TAP_MS && !recording) {
        toggleFacing();
        lastPreviewTap.current = 0;
      } else {
        lastPreviewTap.current = now;
      }
    }
  };

  const onPreviewPointerMove = (e) => {
    if (!previewPointers.current.has(e.pointerId)) return;
    previewPointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (previewPointers.current.size === 2 && pinchBaseDist.current) {
      const pts = Array.from(previewPointers.current.values());
      const dx = pts[1].x - pts[0].x;
      const dy = pts[1].y - pts[0].y;
      const dist = Math.hypot(dx, dy) || 1;
      const factor = dist / pinchBaseDist.current;
      applyZoom(pinchBaseZoom.current * factor);
    }
  };

  const onPreviewPointerUp = (e) => {
    previewPointers.current.delete(e.pointerId);
    if (previewPointers.current.size < 2) {
      pinchBaseDist.current = null;
    }
  };

  /* ---------------- shutter unified handler ---------------- */

  const onShutterPointerDown = (e) => {
    if (!ready) return;
    e.preventDefault();
    pressStartYRef.current = e.clientY ?? (e.touches?.[0]?.clientY ?? null);
    baseZoomRef.current = zoom;
    modeRef.current = 'photo-pending';
    holdTimerRef.current = setTimeout(() => {
      if (modeRef.current === 'photo-pending') {
        modeRef.current = 'video';
        startRecording();
      }
      holdTimerRef.current = null;
    }, HOLD_THRESHOLD_MS);
  };

  const onShutterPointerMove = (e) => {
    // Drag finger up/down to zoom while recording (or while holding pre-record)
    if (pressStartYRef.current == null) return;
    const y = e.clientY ?? (e.touches?.[0]?.clientY ?? null);
    if (y == null) return;
    const dy = pressStartYRef.current - y; // up = positive
    const z = baseZoomRef.current + dy * ZOOM_STEP_PER_PX;
    applyZoom(z);
  };

  const onShutterPointerUp = async () => {
    const mode = modeRef.current;
    // Clear the hold timer regardless
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    pressStartYRef.current = null;
    baseZoomRef.current = 1;

    if (mode === 'video') {
      stopRecording();
      return;
    }
    if (mode === 'photo-pending') {
      modeRef.current = 'idle';
      const blob = await capturePhotoBlob();
      if (!blob) return;
      const file = new File([blob], `story_${Date.now()}.jpg`, { type: 'image/jpeg' });
      goToEditor(file, 'image');
    }
  };

  const onShutterPointerCancel = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    pressStartYRef.current = null;
    if (modeRef.current === 'video') {
      stopRecording();
    }
    modeRef.current = 'idle';
  };

  /* ---------------- navigation to editor (preview) ---------------- */

  const goToEditor = (file, mediaType) => {
    stopStream();
    const blobUrl = URL.createObjectURL(file);
    navigate('/story/edit', {
      replace: true,
      state: { mediaFile: file, mediaUrl: blobUrl, mediaType, local: true },
    });
  };

  const handleGallery = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
    goToEditor(file, mediaType);
  };

  /* ---------------- render ---------------- */

  const timerLabel = (() => {
    const s = Math.floor(elapsedMs / 1000);
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  })();

  return (
    <div
      className="fixed inset-0 bg-black z-[10050] overflow-hidden select-none"
      style={{ height: '100dvh', touchAction: 'none' }}
      data-testid="story-camera"
    >
      {/* Transparent gesture layer covering preview — receives pinch + double-tap.
          touchAction:none ensures multi-touch comes through to the pointer handlers. */}
      <div
        className="absolute inset-0 z-[1]"
        onPointerDown={onPreviewPointerDown}
        onPointerMove={onPreviewPointerMove}
        onPointerUp={onPreviewPointerUp}
        onPointerCancel={onPreviewPointerUp}
        style={{ touchAction: 'none' }}
        data-testid="camera-preview-gestures"
      >
        {/* Live preview — CSS zoom for smooth visual feedback */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-200"
          style={{
            transform: `${facing === 'user' ? 'scaleX(-1)' : ''} scale(${capturePulse ? zoom * 0.97 : zoom})`,
            transformOrigin: 'center center',
            transition: capturePulse
              ? 'transform 220ms cubic-bezier(0.16,1,0.3,1)'
              : 'transform 80ms linear',
          }}
          data-testid="story-camera-video"
        />
      </div>

      <div className="absolute top-0 inset-x-0 h-28 bg-gradient-to-b from-black/45 to-transparent pointer-events-none z-[2]" />
      <div className="absolute bottom-0 inset-x-0 h-48 bg-gradient-to-t from-black/65 to-transparent pointer-events-none z-[2]" />
      {flash && <div className="absolute inset-0 bg-white pointer-events-none animate-fade-in z-[3]" />}

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-3 pt-[max(env(safe-area-inset-top),12px)] z-[5]">
        <button
          onClick={() => { stopRecording(); stopStream(); navigate(-1); }}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center active:scale-95"
          data-testid="camera-close"
          aria-label="Close camera"
        >
          <X size={20} className="text-white" />
        </button>
        {!recording && (
          <button
            onClick={toggleTorch}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center active:scale-95"
            data-testid="camera-torch"
            aria-label="Flash"
          >
            {torchOn ? <Zap size={18} className="text-[#FFD60A]" /> : <ZapOff size={18} className="text-white" />}
          </button>
        )}
      </div>

      {error && (
        <div className="absolute top-1/2 left-4 right-4 -translate-y-1/2 bg-black/70 backdrop-blur-md rounded-2xl p-4 text-white text-sm text-center z-[5]" data-testid="camera-error">
          {error}
        </div>
      )}

      {/* Recording indicator: timer + REC pill */}
      {recording && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#FF3B30] rounded-full px-3 py-1.5 shadow-lg z-[5]" data-testid="camera-recording">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span className="text-white text-xs font-bold tabular-nums">{timerLabel}</span>
        </div>
      )}

      {/* Zoom badge (shows when zoom != 1) */}
      {zoom > 1.02 && (
        <div className="absolute top-28 left-1/2 -translate-x-1/2 bg-black/55 backdrop-blur-md px-3 py-1 rounded-full text-white text-xs font-bold z-[5] tabular-nums" data-testid="camera-zoom-badge">
          {zoom.toFixed(1)}×
        </div>
      )}

      {/* Bottom bar */}
      <div className="absolute bottom-0 inset-x-0 px-6 pb-[max(env(safe-area-inset-bottom),24px)] z-[5]">
        <div className="flex items-center justify-between">
          {/* Camera roll thumbnail button */}
          <button
            onClick={() => galleryRef.current?.click()}
            disabled={recording}
            className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center active:scale-95 disabled:opacity-40"
            data-testid="camera-gallery"
            aria-label="Camera roll"
          >
            <ImageIcon size={22} className="text-white" />
          </button>

          {/* Shutter */}
          <button
            onPointerDown={onShutterPointerDown}
            onPointerMove={onShutterPointerMove}
            onPointerUp={onShutterPointerUp}
            onPointerCancel={onShutterPointerCancel}
            onPointerLeave={(e) => {
              // Keep tracking finger move even if it leaves the button,
              // but only cancel if no active pointer.
              if (!e.buttons && modeRef.current !== 'video') onShutterPointerCancel();
            }}
            disabled={!ready}
            className="relative w-[92px] h-[92px] rounded-full flex items-center justify-center disabled:opacity-60"
            data-testid="camera-shutter"
            aria-label="Tap for photo, hold for video"
          >
            {/* red progress ring during recording */}
            {recording && (
              <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100" data-testid="camera-progress-ring">
                <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="4" />
                <circle
                  cx="50"
                  cy="50"
                  r="46"
                  fill="none"
                  stroke="#FF3B30"
                  strokeWidth="4"
                  strokeDasharray={`${recordProgress * 289} 289`}
                  strokeLinecap="round"
                />
              </svg>
            )}
            <span
              className="flex items-center justify-center rounded-full"
              style={{
                width: recording ? '38%' : '86%',
                height: recording ? '38%' : '86%',
                borderRadius: recording ? 8 : 9999,
                background: recording ? '#FF3B30' : '#FFFFFF',
                boxShadow: '0 0 0 3px rgba(255,255,255,0.92)',
                transition: 'all 240ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
            />
          </button>

          {/* Flip cam */}
          <button
            onClick={toggleFacing}
            disabled={recording}
            className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center active:scale-95 disabled:opacity-40"
            data-testid="camera-flip"
            aria-label="Flip camera"
          >
            <RotateCcw size={22} className="text-white" />
          </button>
        </div>

        {showHint && (
          <p className="text-center text-[11px] text-white/70 mt-3 tracking-wide uppercase font-semibold animate-fade-in" data-testid="camera-hint">
            {recording ? 'Slide up to zoom · Release to stop' : 'Tap = Photo · Hold = Video · Pinch to zoom'}
          </p>
        )}
      </div>

      <input
        ref={galleryRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={handleGallery}
        data-testid="camera-gallery-input"
      />
    </div>
  );
}
